import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Tooltip from '@ecp/common/src/components/tooltip/bo/Tooltip';
import { ReactComponent as QuestionIcon } from '@ecp/common/src/assets/icon/icon__tooltip-question.svg';

const cellStyle = css`
  align-items: flex-start;
  min-height: 45px;
  border-bottom: 1px solid ${boTheme.color.table1};
`;

const Label = styled(FlexBox)`
  padding: 13px 14px;
  background-color: ${boTheme.color.table2};
  ${cellStyle};
  ${({ full }) => full && `grid-column-start: 1`}
`;

const Value = styled(FlexBox)`
  flex-direction: ${({ layout }) => layout};
  width: 100%;
  padding: 8px 14px;
  background-color: ${boTheme.color.sub10};
  gap: ${({ gap }) => gap};
  ${cellStyle};
  ${({ full, label }) => full && `grid-column-end: span calc(var(--form-column)${label ? ' - 1' : ''})`};
  ${({ layout }) => layout === 'row' && 'gap: 6px;'};
`;

const LabelText = styled(TextBox)`
  color: ${boTheme.color.sub1};
  font-weight: ${boTheme.font.weight.bold};
  font-size: ${boTheme.font.size.small};
  line-height: 130%;

  ${({ required }) => (required ? 'gap: 3px' : '')};
`;

const ValueText = styled(TextBox)`
  min-height: 28px;
  color: ${boTheme.color.sub1};
  font-weight: ${boTheme.font.weight.regular};
  font-size: ${boTheme.font.size.small};
  line-height: 130%;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  align-items: center;
`;

const HelpIcon = styled(QuestionIcon)`
  width: 18px;
  height: 18px;
  margin-left: 6px;
  flex-shrink: 0;
  cursor: pointer;
`;

const FormField = ({ label, required, full, layout, gap, help, helpValue, children, labelStyle, valueStyle }) => {
  return (
    <>
      {label && (
        <Label style={{ ...labelStyle }}>
          {typeof label === 'string' ? (
            <LabelText required={required} full={full}>
              {label}
            </LabelText>
          ) : (
            label
          )}
          {help && (
            <Tooltip arrow placement={'bottom-start'} value={helpValue}>
              <HelpIcon />
            </Tooltip>
          )}
        </Label>
      )}
      <Value layout={layout} gap={gap} full={full} label={label} required={required} style={{ ...valueStyle }}>
        {typeof children === 'string' ? <ValueText>{children}</ValueText> : children}
      </Value>
    </>
  );
};

FormField.displayName = 'FormField';

FormField.propTypes = {
  children: PropTypes.node,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType, PropTypes.element, PropTypes.node]),
  required: PropTypes.bool,
  full: PropTypes.bool,
  layout: PropTypes.oneOf(['row', 'column']),
  gap: PropTypes.string,
  help: PropTypes.bool,
  helpValue: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.node]),
  labelStyle: PropTypes.object,
  valueStyle: PropTypes.object,
};

FormField.defaultProps = {
  layout: 'row',
  labelStyle: {},
  valueStyle: {},
};

export default FormField;
